import { useCallback, useMemo } from 'react'
import { useCacheSlice, useCacheMutations } from '../hooks'
import { useApi } from '@/shared/utils/request'

/** API response item from /records/restrictedWords?all=true */
export type RestrictedWordRule = {
  pattern: string
  displayName: string
  suggestion: string | null
  warning: boolean
  client_code?: string | null
  matter_id?: string | null
}

/** Processed restricted word for pattern matching */
export type RestrictedWord = {
  word: string
  pattern: RegExp
  displayName: string
  suggestion: string | null
  severity: 'warning' | 'block'
}

/** Convert API response to processed RestrictedWord */
function processRules(rules: RestrictedWordRule[]): RestrictedWord[] {
  const result: RestrictedWord[] = []
  for (const rule of rules) {
    try {
      result.push({
        word: rule.pattern,
        pattern: new RegExp(rule.pattern, 'gi'),
        displayName: rule.displayName,
        suggestion: rule.suggestion,
        severity: rule.warning ? 'warning' : 'block',
      })
    } catch {
      // Skip invalid regex patterns
    }
  }
  return result
}

/** Group rules by scope: firm, client, or matter */
type ScopedRules = {
  firm: RestrictedWordRule[]
  clients: Map<string, RestrictedWordRule[]>
  matters: Map<string, RestrictedWordRule[]>
}

function groupByScope(rules: RestrictedWordRule[]): ScopedRules {
  const firm: RestrictedWordRule[] = []
  const clients = new Map<string, RestrictedWordRule[]>()
  const matters = new Map<string, RestrictedWordRule[]>()

  for (const rule of rules) {
    if (rule.client_code && rule.matter_id) {
      const key = `${rule.client_code}.${rule.matter_id}`
      const arr = matters.get(key)
      if (arr) arr.push(rule)
      else matters.set(key, [rule])
    } else if (rule.client_code) {
      const arr = clients.get(rule.client_code)
      if (arr) arr.push(rule)
      else clients.set(rule.client_code, [rule])
    } else {
      firm.push(rule)
    }
  }

  return { firm, clients, matters }
}

/**
 * Merge rules with precedence: matter > client > firm.
 * Same regex_pattern (case-insensitive) in a higher scope overrides lower.
 */
function mergeWithPrecedence(layers: RestrictedWordRule[][]): RestrictedWord[] {
  const seen = new Map<string, RestrictedWordRule>()

  // layers[0] = highest precedence (matter), layers[last] = lowest (firm)
  for (const layer of layers) {
    for (const rule of layer) {
      const key = rule.pattern.toLowerCase()
      if (!seen.has(key)) {
        seen.set(key, rule)
      }
    }
  }

  return processRules(Array.from(seen.values()))
}

export function useRestrictedWords() {
  const { data, isLoading, error } = useCacheSlice('restricted_words')
  const { refresh } = useCacheMutations()
  const { makeApiRequest } = useApi()

  // Group all rules by scope (memoized)
  const scoped = useMemo(
    () => groupByScope(data as RestrictedWordRule[]),
    [data],
  )

  /**
   * Get restricted words for a given scope with precedence merge.
   *   - both null → firm-wide only
   *   - client only → client + firm (client overrides firm)
   *   - client + matter → matter + client + firm (matter overrides client overrides firm)
   */
  const getRestrictedWords = useCallback(
    (client?: string, matter?: string): RestrictedWord[] => {
      const layers: RestrictedWordRule[][] = []

      if (client && matter) {
        layers.push(scoped.matters.get(`${client}.${matter}`) ?? [])
      }
      if (client) {
        layers.push(scoped.clients.get(client) ?? [])
      }
      layers.push(scoped.firm)

      return mergeWithPrecedence(layers)
    },
    [scoped],
  )

  /**
   * Fetch for settings UI — includes ALL matter-specific rules for a client (no dedup).
   * This is a special mode not used by the normal flow.
   */
  const fetchAllForClient = useCallback(
    async (clientCode: string, matterCode?: string): Promise<RestrictedWord[]> => {
      const params = new URLSearchParams()
      params.set('client_code', clientCode)
      if (matterCode) params.set('matter_code', matterCode)
      params.set('include_matter_specific', 'true')

      const endpoint = `/records/restrictedWords?${params.toString()}`
      const res = await makeApiRequest({ endpoint, method: 'GET' })
      return processRules((res.data ?? []) as RestrictedWordRule[])
    },
    [makeApiRequest],
  )

  return {
    getRestrictedWords,
    fetchAllForClient,
    isLoading,
    error,
    refresh: () => refresh('restricted_words'),
  }
}
